import { useEffect, useState, useCallback, useMemo } from "react";
import { type MessageSizerRequest, type MessageSizerResponse } from "#/workers/message-sizer";
import MessageSizerWorker from "#/workers/message-sizer?worker";

/**
 * Message input for sizing calculation
 */
export interface MessageInput {
	/** Unique identifier for the message (typically the label GID) */
	id: string;
	/** The message text to size */
	text: string;
}

/**
 * Calculated font size for a message
 */
export interface MessageSize {
	/** Unique identifier matching the input message */
	id: string;
	/** Font size in cqi units */
	fontSize: number;
	/** True when layout is tight and likely to overflow on more characters */
	overflowHint: boolean;
}

/**
 * Configuration for label dimensions and padding
 * Padding values match Label.MessageOverlay and Label.MessageText combined
 */
export interface MessageSizerConfig {
	/** Label width in inches (default: 8) */
	labelWidth?: number;
	/** Label height in inches (default: 4) */
	labelHeight?: number;
	/** DPI for canvas rendering (default: 96) */
	dpi?: number;
	/** Font family (default: "system-ui, sans-serif") */
	fontFamily?: string;
	/** Font weight (default: 600) */
	fontWeight?: number;
	/** Line height multiplier (default: 1.3) */
	lineHeight?: number;
	/** Enable debug mode to render first message preview */
	debug?: boolean;
	/** [TESTING ONLY] Artificial delay in ms before returning results */
	testDelay?: number;
	/** [TESTING ONLY] Force an error state for testing error UI */
	forceError?: boolean;
}

/**
 * Debug info returned when debug mode is enabled
 */
export interface MessageSizerDebugInfo {
	fontUsed: string;
	fontLoaded: boolean;
	containerWidth: number;
	containerHeight: number;
	textAreaWidth: number;
	textAreaHeight: number;
}

/**
 * Return type for useMessageSizes hook
 */
export interface UseMessageSizesReturn {
	/** Map of message ID to font size in cqi */
	sizes: Map<string, number>;
	/** Map of message ID to overflow hint */
	overflowHints: Map<string, boolean>;
	/** Current status of the calculation */
	status: "idle" | "loading" | "ready" | "error";
	/** Error message if status is "error" */
	error: string | null;
	/** Retry the calculation */
	retry: () => void;
	/** Get font size for a specific message ID (returns default if not found) */
	getSize: (id: string) => number;
	/** Get overflow hint for a specific message ID */
	getOverflowHint: (id: string) => boolean;
	/** Debug image as base64 data URL (only present if debug mode enabled) */
	debugImage: string | null;
	/** Debug info about the calculation */
	debugInfo: MessageSizerDebugInfo | null;
}

/**
 * Default padding values derived from design system recipes:
 * - labelMessageOverlay: paddingInline: 5cqw, paddingBlock: 9.5cqh
 * - labelMessageText: padding: 4cqh, paddingBottom: 18cqh
 *
 * Combined values (as percentages for calculation):
 * - Inline (left/right): 5% (cqw)
 * - Block start (top): 9.5% + 4% = 13.5% (cqh)
 * - Block end (bottom): 9.5% + 18% = 27.5% (cqh)
 */
const DEFAULT_PADDING = {
	inlineCqw: 5,
	blockStartCqh: 13.5,
	blockEndCqh: 27.5,
} as const;

/** Default font size in cqi when no calculation is available */
const DEFAULT_FONT_SIZE = 20;

/**
 * Hook to calculate optimal font sizes for message labels
 *
 * Sends messages to a web worker that creates an 8"x4" canvas,
 * applies the combined padding from Label.MessageOverlay and Label.MessageText,
 * then binary searches for the largest font size that doesn't overflow.
 *
 * @param messages - Array of messages to calculate sizes for
 * @param config - Optional configuration overrides
 * @returns Object with sizes map, status, and utility functions
 *
 * @example
 * ```tsx
 * const { sizes, status, getSize } = useMessageSizes(
 *   labels.map(l => ({ id: l.gid, text: l.message ?? "" }))
 * );
 *
 * // In render:
 * <Label.MessageText style={{ fontSize: `${getSize(label.gid)}cqi` }}>
 *   {label.message}
 * </Label.MessageText>
 * ```
 */
export function useMessageSizes(
	messages: MessageInput[],
	config: MessageSizerConfig = {},
): UseMessageSizesReturn {
	const [sizes, setSizes] = useState<Map<string, number>>(new Map());
	const [overflowHints, setOverflowHints] = useState<Map<string, boolean>>(new Map());
	const [status, setStatus] = useState<"idle" | "loading" | "ready" | "error">("idle");
	const [error, setError] = useState<string | null>(null);
	const [retryTrigger, setRetryTrigger] = useState(0);
	const [debugImage, setDebugImage] = useState<string | null>(null);
	const [debugInfo, setDebugInfo] = useState<MessageSizerDebugInfo | null>(null);

	// Memoize config with defaults
	// Font string matches manifest.ts format exactly
	const resolvedConfig = useMemo(
		() => ({
			labelWidth: config.labelWidth ?? 8,
			labelHeight: config.labelHeight ?? 4,
			dpi: config.dpi ?? 96,
			fontFamily: config.fontFamily ?? '"Poppins", "Segoe UI", sans-serif',
			fontWeight: config.fontWeight ?? 600,
			lineHeight: config.lineHeight ?? 1.3,
			debug: config.debug ?? false,
		}),
		[
			config.labelWidth,
			config.labelHeight,
			config.dpi,
			config.fontFamily,
			config.fontWeight,
			config.lineHeight,
			config.debug,
		],
	);

	// Create stable message key for dependency checking
	const messagesKey = useMemo(() => messages.map((m) => `${m.id}:${m.text}`).join("|"), [messages]);

	useEffect(() => {
		// Skip if no messages
		if (messages.length === 0) {
			setSizes(new Map());
			setOverflowHints(new Map());
			setStatus("idle");
			return;
		}

		let cancelled = false;

		function calculateSizes() {
			setStatus("loading");
			setError(null);

			const worker = new MessageSizerWorker();

			const timeout = setTimeout(() => {
				worker.terminate();
				if (!cancelled) {
					setError("Message sizing timeout");
					setStatus("error");
				}
			}, 30000);

			worker.onmessage = (e: MessageEvent<MessageSizerResponse>) => {
				clearTimeout(timeout);

				if (cancelled) {
					worker.terminate();
					return;
				}

				if (e.data.type === "calculated") {
					const newSizes = new Map<string, number>();
					const newHints = new Map<string, boolean>();
					for (const sizeEntry of e.data.payload.sizes) {
						newSizes.set(sizeEntry.id, sizeEntry.fontSize);
						newHints.set(
							sizeEntry.id,
							"overflowHint" in sizeEntry
								? Boolean((sizeEntry as { overflowHint?: boolean }).overflowHint)
								: false,
						);
					}
					setSizes(newSizes);
					setOverflowHints(newHints);
					setStatus("ready");

					// Set debug data if available
					if (e.data.payload.debugImage) {
						setDebugImage(e.data.payload.debugImage);
					}
					if (e.data.payload.debugInfo) {
						setDebugInfo(e.data.payload.debugInfo);
					}
				} else if (e.data.type === "error") {
					setError(e.data.payload.message);
					setStatus("error");
				}

				worker.terminate();
			};

			worker.onerror = (err) => {
				clearTimeout(timeout);
				worker.terminate();

				if (!cancelled) {
					setError(err.message);
					setStatus("error");
				}
			};

			const request: MessageSizerRequest = {
				type: "calculate",
				payload: {
					messages: messages.map((m) => ({ id: m.id, text: m.text })),
					labelWidth: resolvedConfig.labelWidth,
					labelHeight: resolvedConfig.labelHeight,
					dpi: resolvedConfig.dpi,
					padding: DEFAULT_PADDING,
					font: {
						family: resolvedConfig.fontFamily,
						weight: resolvedConfig.fontWeight,
						lineHeight: resolvedConfig.lineHeight,
					},
					debug: resolvedConfig.debug,
					testDelay: config.testDelay,
					forceError: config.forceError,
				},
			};

			worker.postMessage(request);
		}

		calculateSizes();

		return () => {
			cancelled = true;
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps -- Only re-run on messages, config changes, or explicit retry
	}, [messagesKey, resolvedConfig, retryTrigger]);

	const retry = useCallback(() => {
		if (status === "error") {
			setRetryTrigger((c) => c + 1);
		}
	}, [status]);

	const getSize = useCallback(
		(id: string): number => {
			return sizes.get(id) ?? DEFAULT_FONT_SIZE;
		},
		[sizes],
	);

	const getOverflowHint = useCallback(
		(id: string): boolean => {
			return overflowHints.get(id) ?? false;
		},
		[overflowHints],
	);

	return {
		sizes,
		overflowHints,
		status,
		error,
		retry,
		getSize,
		getOverflowHint,
		debugImage,
		debugInfo,
	};
}
